import { Button, DatePicker, Form, Input, message, Modal, notification, Table, Tooltip, Typography } from "antd";
import { FormComponentProps } from "antd/lib/form";
import moment, { Moment } from "moment";
import React, { FormEvent } from "react";
import { Page } from '../../components'
import { userService } from "../../services";
import { ApiToken, ApiTokenCreateFormValues } from "../../types";
import styles from "./index.module.less"


interface FormProps extends FormComponentProps {
	submit(values: ApiTokenCreateFormValues): Promise<void>
}

function hasErrors(fieldsError: Record<string, string[]>) {
	return Object.keys(fieldsError).some(field => fieldsError[field]);
}
const NewTokenForm: React.FC<FormProps> = (props) => {
	const [loading, setLoading] = React.useState(false)
	const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
		setLoading(true)
		e.preventDefault()
		props.form.validateFields(
			async (errors, values): Promise<void> => {
				if (errors) {
					return
				}
				await props.submit({ ...values })
			}
		)
		setLoading(false)
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const dateMustBeGreaterThanToday = (_: any, value: Moment | undefined, callback: any) => {
		if (value && value.isSameOrBefore(moment())) {
			return callback('Expiry must be in the future')
		}
		return callback()
	}

	const disableDatesBeforeToday = (current: Moment) => {
		return current.isSameOrBefore(moment())
	}

	return (
		<Form colon={false} onSubmit={onSubmit} layout='horizontal' labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} labelAlign='left'>
			<Form.Item hasFeedback label="Name">
				{props.form.getFieldDecorator('name', {
					rules: [{ whitespace: true, required: true, message: 'Name is required' }]
				})(<Input placeholder="Enter name" disabled={loading} />)}
			</Form.Item>
			<Form.Item label="Expiry">
				{props.form.getFieldDecorator('expires_at', {
					rules: [{ validator: dateMustBeGreaterThanToday, message: 'Expiry must be in the future' }]
				})(<DatePicker placeholder="Enter expiry" disabled={loading} disabledDate={disableDatesBeforeToday} allowClear style={{ width: '100%' }} />)}
			</Form.Item>
			<Form.Item label=' ' className={styles.submitButton}>
				<Button loading={loading} type="primary" htmlType="submit" disabled={hasErrors(props.form.getFieldsError()) || loading}>
					Create
				</Button>
			</Form.Item>
		</Form>
	)
}

const WrappedForm = Form.create<FormProps>()(NewTokenForm)

interface TokenSuccessDescriptionProps {
	value: string
}
const TokenSuccessDescription: React.FC<TokenSuccessDescriptionProps> = ({ value }) => {
	const copy = async () => {
		try {
			await window.navigator.clipboard.writeText(value)
			message.info("Copied to clipboard.")
		} catch (err) {
			message.error("Unable to copy to clipboard. Please copy manually.")
		}
	}
	return (
		<div>
			<Typography.Paragraph>Make sure to copy your personal access token now as you will not be able to see this again.</Typography.Paragraph>
			<pre className={styles.token}>
				{value}
			</pre>
			<Button icon="copy" onClick={copy} className={styles.copyToClipboard} type="link">Copy to clipboard</Button>
		</div>
	)
}

interface ExpiryDateProps {
	date?: Moment
}
const ExpiryDate: React.VFC<ExpiryDateProps> = ({ date }) => {
	if (!date) {
		return <Typography.Text type='warning' strong>Never</Typography.Text>
	} else if (date.isSameOrBefore(moment())) {
		return <Tooltip title="This token has expired"><Typography.Text type='danger'>{date.format('MMMM Do YYYY')}</Typography.Text></Tooltip>
	} else {
		return <Typography.Text>{date.format('MMMM Do YYYY')}</Typography.Text>
	}
}

const ApiTokens: React.FC = () => {
	const [tokens, setTokens] = React.useState<ApiToken[]>([])
	const [loading, setLoading] = React.useState<boolean>(true)
	const [modalOpen, setModalOpen] = React.useState<boolean>(false)
	const getTokens = async () => {
		try {
			setLoading(true)
			const result = await userService.getTokens()
			setTokens(result.data)
		} catch (error) {
			message.error('Something went wrong when attempting to fetch the tokens. Refresh the page to try again.')
		} finally {
			setLoading(false)
		}
	}

	const deleteToken = async (token: ApiToken) => {
		try {
			await userService.deleteToken(token.id)
			message.success("Deleted token successfully.")
		} catch (err) {
			message.error("Failed to delete token. Please try again.")
		} finally {
			await getTokens()
		}
	}

	const createToken = async (values: ApiTokenCreateFormValues) => {
		try {
			const response = await userService.createToken(values)
			notification.success({
				message: `Token "${values.name}" created.`,
				description: <TokenSuccessDescription value={response.data} />,
				duration: null,
			})
			setModalOpen(false)
		} catch (err) {
			message.error("Failed to create token. Please try again.")
		} finally {
			await getTokens()
		}
	}

	React.useEffect(() => {
		getTokens()
	}, [])

	const actions = (token: ApiToken) => {
		return <Button
			type='danger'
			icon='delete'
			key='delete'
			title={`Delete ${token.name}`}
			onClick={() => Modal.confirm({
				title: `Are you sure you want to delete this token?`,
				okType: 'danger',
				okText: 'Confirm',
				async onOk() {
					await deleteToken(token)
				}
			})}
		>
			Delete
		</Button>
	}

	return (
		<Page title="API Tokens">
			<Button onClick={() => setModalOpen(true)} className={styles.button} icon="plus">Create Token</Button>
			<Table dataSource={tokens} loading={loading} rowKey='id'>
				<Table.Column title="Name" dataIndex="name" />
				<Table.Column title="Last Used At" dataIndex="last_used_at" render={text => text ? moment(text).fromNow() : undefined} />
				<Table.Column title="Expires On" dataIndex="expires_at" render={text => <ExpiryDate date={text ? moment(text) : undefined} />} />
				<Table.Column title="" render={(_, token: ApiToken) => actions(token)} />
			</Table>
			<Modal destroyOnClose title="Create Token" visible={modalOpen} closable onCancel={() => setModalOpen(false)} footer={null}>
				<WrappedForm submit={createToken} />
			</Modal>
		</Page>
	)
}

export default ApiTokens
